import { SIGNED_IN_HINT_KEY } from 'src/constants';

/**
 * The account session since the single sign-in (api#454): a cookie the api sets after the emailed
 * code — `HttpOnly`, so no script on this page, this file included, can read it — and the dealer
 * cabinet holds the very same one. The storefront keeps two things of its own about it:
 *
 *  - a hint in `localStorage` that this browser signed in here. Not a credential: it only tells the
 *    shell to hold the splash and the Buy control until `/user/auth/me` has answered, instead of
 *    showing a signed-in person the anonymous page for a frame. A wrong hint costs one refused `/me`,
 *    which clears it. Without a hint the page renders for a guest and still asks `/me` once — that is
 *    how a dealer who signed in in the cabinet is found signed in here;
 *  - a count of the sign-ins and sign-outs of this tab, the epoch. `/user/auth/me` and every read of
 *    a person's own things are keyed by it, so a session that ends and another that begins in the
 *    same tab never share a cached answer (app#248) — the token used to be that key, and there is
 *    no token to key by any more.
 *
 * Changes are announced on a custom event so `useUserSession` re-renders across routes; another
 * tab's sign-in or sign-out arrives by the browser's own `storage` event, and counts as an epoch
 * of this tab too — the cookie is one per browser, so what happened there happened here.
 *
 * Storage that cannot be read or written — blocked by the browser's settings, some embedded
 * webviews — makes the visitor one with no hint rather than breaking anything: the api's cookie
 * does not depend on it, and `/me` is asked all the same.
 */
export const USER_SESSION_EVENT = 'gac:user-session-changed';

/** The snapshot of a tab that has not hydrated yet — what the server renders with. */
export const SERVER_SESSION_SNAPSHOT = '0:0';

let epoch = 0;

/**
 * The browser's storage, or `null` where reading the property itself throws — a browser that blocks
 * storage, some embedded webviews. The callers that take a store treat `null` as a visitor with none.
 */
export const localStore = (): Storage | null => {
  try {
    return localStorage;
  } catch {
    return null;
  }
};

const read = (key: string): string | null => {
  try {
    return localStore()?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const write = (key: string, value: string | null): void => {
  try {
    const store = localStore();

    if (!store) return;
    if (value === null) {
      store.removeItem(key);
    } else {
      store.setItem(key, value);
    }
  } catch {
    // Unreadable storage is a visitor with no hint (see above); a hint that cannot be kept is not kept.
  }
};

const announce = (): void => {
  window.dispatchEvent(new Event(USER_SESSION_EVENT));
};

/** Whether this browser signed in here — the hint, not the session. */
export const hasSignedInHint = (): boolean => read(SIGNED_IN_HINT_KEY) === '1';

/** How many sign-ins and sign-outs this tab has seen: the key of every session read. */
export const sessionEpoch = (): number => epoch;

/** The epoch and the hint as one value, so a store compares it by value: `"<epoch>:<0|1>"`. */
export const sessionSnapshot = (): string => `${epoch}:${hasSignedInHint() ? 1 : 0}`;

/** A sign-in happened in this tab: the hint is set, and every session read starts over. */
export const markSignedIn = (): void => {
  write(SIGNED_IN_HINT_KEY, '1');
  epoch += 1;
  announce();
};

/** A sign-out happened in this tab: the hint goes, and every session read starts over. */
export const markSignedOut = (): void => {
  write(SIGNED_IN_HINT_KEY, null);
  epoch += 1;
  announce();
};

/**
 * The api refused the session this tab believed in: the hint was wrong and goes. Not a new epoch —
 * the refusal is this epoch's answer, and asking again would ask forever.
 */
export const dropSignedInHint = (): void => {
  if (!hasSignedInHint()) return;

  write(SIGNED_IN_HINT_KEY, null);
  announce();
};

/**
 * Another tab's sign-in or sign-out (`storage`, for the hint's key or a wholesale clear) is a new
 * epoch here as well: the cookie it signed in or out with is this tab's too. One listener for the
 * whole tab, installed with the first subscriber: a listener per subscriber would bump the epoch
 * once per mounted hook, and a count of sign-ins would be a count of hooks.
 */
let subscribers = 0;

const onStorage = (event: StorageEvent): void => {
  if (event.key !== null && event.key !== SIGNED_IN_HINT_KEY) return;

  epoch += 1;
  announce();
};

/** Subscribe to session changes: this tab's and another tab's, both announced on the custom event. */
export const subscribeUserSession = (callback: () => void): (() => void) => {
  window.addEventListener(USER_SESSION_EVENT, callback);
  if (subscribers === 0) window.addEventListener('storage', onStorage);
  subscribers += 1;

  return () => {
    window.removeEventListener(USER_SESSION_EVENT, callback);
    subscribers -= 1;
    if (subscribers === 0) window.removeEventListener('storage', onStorage);
  };
};
